import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Addon, Agreement } from '../schemas';
import { Auth } from '../service';
import { AddonForm, AddonFormData } from './addon-form';
import { RemoveAddonData, RemoveAddonDialog } from './remove-addon-dialog';

@Component({
  selector: 'lcm-addon-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTableModule, MatIconModule, MatToolbarModule, MatButtonModule, MatTooltipModule, DatePipe, DecimalPipe],
  template: `
    <mat-toolbar>
      <h3>Addons</h3>
      <span class="spacer"></span>
      @if (auth.isAdmin()) {
        <button matMiniFab (click)="add()" [disabled]="pending()">
          <mat-icon>add</mat-icon>
        </button>
      }
    </mat-toolbar>

    <table mat-table [dataSource]="source()" [trackBy]="trackBy">
      <mat-text-column name="AddonName" headerText="Addon Name" />
      <mat-text-column name="Status" headerText="Status" />
      <mat-text-column name="BillingCycle" headerText="Billin Cycle" />

      <ng-container matColumnDef="StartDate">
        <th mat-header-cell *matHeaderCellDef>Start Date</th>
        <th></th>
        <td mat-cell *matCellDef="let addon">{{ addon.StartDate | date: 'dd/MM/y' }}</td>
      </ng-container>

      <ng-container matColumnDef="EndDate">
        <th mat-header-cell *matHeaderCellDef>End Date</th>
        <th></th>
        <td mat-cell *matCellDef="let addon">{{ addon.EndDate | date: 'dd/MM/y' }}</td>
      </ng-container>

      <ng-container matColumnDef="InvoiceAmount">
        <th mat-header-cell *matHeaderCellDef>Invoice Amount</th>
        <th></th>
        <td mat-cell *matCellDef="let addon">\${{ addon.InvoiceAmount | number: '1.2' }}</td>
      </ng-container>

      <ng-container matColumnDef="InvoiceSeparately">
        <th mat-header-cell *matHeaderCellDef>Invoice Separately</th>
        <th></th>
        <td mat-cell *matCellDef="let addon">{{ addon.InvoiceSeparately ? 'Yes' : 'No' }}</td>
      </ng-container>

      <ng-container matColumnDef="NextInvoiceDate">
        <th mat-header-cell *matHeaderCellDef>Next Invoice</th>
        <th></th>
        <td mat-cell *matCellDef="let addon" [class.mat-text-error]="addon.NextInvoiceDate < now()">
          {{ addon.NextInvoiceDate | date: 'dd/MM/y' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>Actions</th>
        <th></th>
        <td mat-cell *matCellDef="let addon">
          @if (auth.isAdmin()) {
            <button [disabled]="pending()" matMiniFab (click)="remove(addon)">
              <mat-icon>delete</mat-icon>
            </button>
          }
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="columnsToShow()"></tr>
      <tr mat-row *matRowDef="let addon; columns: columnsToShow()"></tr>
    </table>
  `,
  styles: ``,
})
export class AddonTable {
  columnsToShow = signal([
    'AddonName',
    'StartDate',
    'EndDate',
    'BillingCycle',
    'Status',
    'InvoiceAmount',
    'InvoiceSeparately',
    'NextInvoiceDate',
    'actions',
  ]);

  agreement = input.required<Agreement>();

  dialog = inject(MatDialog);

  auth = inject(Auth);

  now = signal(new Date().toISOString());

  removed = output();

  added = output();

  source = computed(() => this.agreement().Addons || []);

  trackBy(idx: number, row: Addon) {
    return row.Id;
  }

  pending = input(false);

  add() {
    const data: AddonFormData = { agreement: this.agreement() };

    this.dialog
      .open(AddonForm, { data })
      .afterClosed()
      .subscribe({
        next: (result) => result && this.added.emit(),
      });
  }

  remove(addon: Addon) {
    const data: RemoveAddonData = { addon, agreement: this.agreement() };
    this.dialog
      .open(RemoveAddonDialog, { data })
      .afterClosed()
      .subscribe({
        next: (result) => result && this.removed.emit(),
      });
  }
}
